import { useRoute, Link, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { isDuplicateContactBody, toastDuplicateContact } from "@/lib/duplicateContact";
import { AppLayout } from "@/components/layout/AppLayout";
import { ChangeCompanyDialog, type PickableCompany } from "@/components/ChangeCompanyDialog";
import { AddToDealDialog } from "@/components/AddToDealDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  User,
  Edit,
  MoreHorizontal,
  Mail,
  Phone,
  FileText,
  Building2,
  ArrowLeft,
  Loader2,
  Briefcase,
  Trash2,
  Handshake,
  Linkedin,
  Pencil,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ActivityTimeline } from "@/components/activity/ActivityTimeline";
import { RailDocuments } from "@/components/record/RailDocuments";
import { EditContactDrawer } from "@/components/edit/EditContactDrawer";
import { InlineEdit } from "@/components/InlineEdit";
import { useContactField } from "@/hooks/useContactField";
import { EntityRemindersButton } from "@/components/reminders/EntityRemindersButton";
import { useCurrentUserTimezone } from "@/hooks/useCurrentUserTimezone";
import { useRecordCompose } from "@/components/record/useRecordCompose";
import { sourceLabel } from "@/components/record/recordFormat";
import { RecordFrame, RecordPanel } from "@/components/record/RecordFrame";
import { RecordHeader, RecordChip } from "@/components/record/RecordHeader";
import { ReachItem } from "@/components/record/ReachItem";
import { FactsStrip } from "@/components/record/FactsStrip";
import { ActionBar } from "@/components/record/ActionBar";
import { BriefPanel } from "@/components/record/BriefPanel";
import { RailPeople, type RailPerson } from "@/components/record/RailPeople";
import { RailFtcContact } from "@/components/record/RailFtcContact";
import { RailDetails } from "@/components/record/RailDetails";
import { RailNotes } from "@/components/record/RailNotes";
import { ProcessesPanel } from "@/components/record/ProcessesPanel";
import { contactFacts } from "@/components/record/facts/personFacts";
import { firstName, localYmd } from "@/components/record/recordFormat";
import { useContactSummary } from "@/hooks/useRecordSummary";

interface CreatorUser {
  id: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
}

interface Contact {
  id: string;
  companyId: string;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  isPrimary: boolean;
  notes: string | null;
  employerName: string | null;
  createdAt: string;
  createdBy: string | null;
  creationSource: "manual" | "business_card" | "bulk_upload" | "outlook" | "chrome_extension" | null;
  contactType?: "general" | "coi" | null;
  willingToAdvocate?: boolean;
  creator?: CreatorUser | null;
}

interface Company {
  id: string;
  name: string;
  category: string;
  industry: string | null;
  website: string | null;
  contacts?: RailPerson[];
}

const getCompanyRoute = (category: string) => {
  switch (category) {
    case 'Buyer Network':
      return 'buyers';
    case 'Client':
    case 'Sell-Side':
      return 'clients';
    case 'Prospects':
      return 'prospects';
    case 'COI':
      return 'companies';
    case 'Capital Providers':
      return 'capital-providers';
    default:
      return 'companies';
  }
};

export default function ContactDetail() {
  const [match, params] = useRoute("/contacts/:id");
  const [, setLocation] = useLocation();
  const id = params?.id || "";
  const queryClient = useQueryClient();
  const userTz = useCurrentUserTimezone();
  const { saveField: saveContactField, saveFieldAsync: saveContactFieldAsync } = useContactField(id);
  const [editOpen, setEditOpen] = useState(false);
  const [changeCompanyOpen, setChangeCompanyOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addToDealOpen, setAddToDealOpen] = useState(false);
  // The action bar and the L key open the log form on the activity timeline.
  const { compose, setCompose, openLog, nextStepOpen, setNextStepOpen } = useRecordCompose();

  // Shared handler used by both "Change company" (has existing affiliation)
  // and "Link to company" (no affiliation yet). PATCH /api/contacts/:id
  // accepts { companyId } — already supported server-side at routes.ts:5105.
  const handleCompanyPick = async (company: PickableCompany) => {
    try {
      const res = await fetch(`/api/contacts/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId: company.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        if (isDuplicateContactBody(body)) toastDuplicateContact(body, setLocation);
        else toast.error(body?.error ?? "Failed to update company");
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["contact", id] });
      queryClient.invalidateQueries({ queryKey: ["company", company.id] });
      toast.success(`Moved to ${company.name}`);
    } catch (err) {
      toast.error("Failed to update company");
    }
  };

  // Export the contact as a vCard 3.0 .vcf download. Opens directly in
  // macOS Contacts / Google Contacts / iOS / Outlook — much more useful
  // than a plain-text dump because the recipient can save it to their
  // own address book with one click.
  //
  // vCard escaping rules: backslash, comma, semicolon, and newlines in
  // field values must be escaped. Newlines become literal "\n". Empty
  // fields are omitted rather than sent as empty strings so import
  // dialogs don't show blank fields.
  const handleExport = () => {
    if (!contact) return;
    const esc = (s: string | null | undefined): string =>
      s == null ? "" : String(s).replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\r?\n/g, "\\n");

    // Split full name into first/last. If there's only one token, treat
    // it as the first name; the last-name slot stays empty (valid vCard).
    const nameTokens = (contact.name || "").trim().split(/\s+/);
    const firstName = nameTokens[0] || "";
    const lastName = nameTokens.length > 1 ? nameTokens.slice(1).join(" ") : "";

    const lines: string[] = ["BEGIN:VCARD", "VERSION:3.0"];
    lines.push(`N:${esc(lastName)};${esc(firstName)};;;`);
    lines.push(`FN:${esc(contact.name)}`);
    if (contact.title) lines.push(`TITLE:${esc(contact.title)}`);
    if (company?.name) lines.push(`ORG:${esc(company.name)}`);
    if (contact.email) lines.push(`EMAIL;TYPE=WORK,INTERNET:${esc(contact.email)}`);
    if (contact.phone) lines.push(`TEL;TYPE=WORK,VOICE:${esc(contact.phone)}`);
    if ((contact as any).linkedinUrl) lines.push(`URL:${esc((contact as any).linkedinUrl)}`);
    if ((contact as any).notes) lines.push(`NOTE:${esc((contact as any).notes)}`);
    lines.push("END:VCARD");

    const blob = new Blob([lines.join("\r\n")], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(contact.name || "contact").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Exported as vCard");
  };

  // DELETE /api/contacts/:id hard-deletes the row (server handler at
  // routes.ts:5128). Before this change, the "Delete contact" menu item
  // was a dead button — no onClick, no confirmation, no network call.
  // Caleb: "deleting a contact doesnt really work like it should. There
  // is no indication that it was successful and it doesnt even work when
  // you click delete."
  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`/api/contacts/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Invalidate both the single contact (now gone) and any list queries
      // that might be showing this row. Using a predicate rather than an
      // exhaustive list of query keys so e.g. Contacts.tsx, CompanyDetail's
      // contact list, and COI.tsx all refresh.
      queryClient.invalidateQueries({ queryKey: ["contact", id] });
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          (q.queryKey[0] === "contacts" || q.queryKey[0] === "company"),
      });
      toast.success("Contact deleted");
      setDeleteDialogOpen(false);
      // Navigate away — the detail page for a now-deleted contact would
      // 404 on its query. Fall back to the contacts directory.
      setLocation("/contacts");
    } catch (err) {
      toast.error("Failed to delete contact");
    } finally {
      setIsDeleting(false);
    }
  };

  const { data: contact, isLoading: contactLoading, error: contactError } = useQuery<Contact>({
    queryKey: ["contact", id],
    queryFn: async () => {
      const response = await fetch(`/api/contacts/${id}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch contact");
      }
      return response.json();
    },
    enabled: !!id,
  });
  // The COI label is an upgrade on the contact: every link to the plain page lands on the COI view while it is set.
  useEffect(() => { if (contact?.contactType === "coi") setLocation(`/coi/${contact.id}`, { replace: true }); }, [contact?.contactType, contact?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: company, isLoading: companyLoading } = useQuery<Company>({
    queryKey: ["company", contact?.companyId],
    queryFn: async () => {
      const response = await fetch(`/api/companies/${contact?.companyId}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch company");
      }
      return response.json();
    },
    enabled: !!contact?.companyId,
  });

  const summary = useContactSummary(contact?.id);

  const isLoading = contactLoading || (contact?.companyId && companyLoading);

  if (isLoading) {
    return (
      <AppLayout title="Loading...">
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-12 h-12 text-primary-text animate-spin mb-4" />
          <p className="text-muted-foreground">Loading contact details...</p>
        </div>
      </AppLayout>
    );
  }

  if (contactError || !contact) {
    return (
      <AppLayout title="Contact Not Found">
        <div className="flex flex-col items-center justify-center py-16">
          <User className="w-16 h-16 text-muted-foreground/30 mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Contact Not Found</h2>
          <p className="text-muted-foreground mb-6">The contact you're looking for doesn't exist.</p>
          <Link href="/contacts">
            <Button className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Contacts
            </Button>
          </Link>
        </div>
      </AppLayout>
    );
  }

  const category = company?.category ?? null;
  const companyHref = contact.companyId && company && category ? `/${getCompanyRoute(category)}/${contact.companyId}` : null;
  const colleagues: RailPerson[] = (company?.contacts ?? []).filter((p) => p.id !== contact.id);
  const today = localYmd(new Date());

  const header = (
    <RecordHeader
      crumb={{ label: "All Contacts", href: "/contacts" }}
      current={contact.name}
      name={
        <InlineEdit label="Contact name"
          value={contact.name}
          onSave={(v) => { const t = v.trim(); if (!t) throw new Error("Name is required"); return saveContactFieldAsync("name", t); }}
          displayClassName="cursor-pointer rounded px-1 -mx-1 hover:bg-primary/5 inline-flex items-center gap-1"
          inputClassName="w-full rounded border bg-background px-1 py-0.5 text-[22px] font-bold outline-none"
        />
      }
      chips={<>
        {category && <RecordChip tone="category">{category === "Buyer Network" ? "Buyer contact" : category === "COI" ? "Other company" : category}</RecordChip>}
        {contact.isPrimary && <RecordChip>Primary</RecordChip>}
        {contact.willingToAdvocate && <RecordChip tone="gold">Advocate</RecordChip>}
      </>}
      subline={[
        { icon: <Briefcase />, content: <InlineEdit label="Job title" value={contact.title} placeholder="Add title" onSave={(v) => saveContactFieldAsync("title", v.trim() || null)} /> },
        companyHref && company
          ? { icon: <Building2 />, content: (
              <span className="group inline-flex items-center gap-1">
                <Link href={companyHref} className="hover:text-foreground hover:underline" data-testid="company-link">{company.name}</Link>
                <button type="button" onClick={() => setChangeCompanyOpen(true)} aria-label="Change company" className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100">
                  <Pencil className="h-3 w-3" />
                </button>
              </span>
            ) }
          : { icon: <Building2 />, content: <button type="button" onClick={() => setChangeCompanyOpen(true)} className="text-primary-text hover:underline">Link to company</button> },
      ]}
      reach={<>
        {contact.email && <ReachItem icon={<Mail />} value={contact.email} />}
        {contact.phone && <ReachItem icon={<Phone />} value={contact.phone} />}
        {contact.linkedinUrl && <ReachItem icon={<Linkedin />} value="LinkedIn" href={contact.linkedinUrl} copy={false} />}
      </>}
      actions={
        <ActionBar onLog={openLog} email={contact.email} onNextStep={() => setNextStepOpen(true)}>
          <EntityRemindersButton
            entityType="contact"
            entityId={contact.id}
            entityName={contact.name}
            userTimezone={userTz}
          />
          <Button size="sm" variant="outline" className="gap-1.5" data-testid="edit-contact" onClick={() => setEditOpen(true)}>
            <Edit className="h-3.5 w-3.5" />
            Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-8 min-h-0 p-0" data-testid="more-actions">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {contact.contactType !== "coi" && (
                <DropdownMenuItem
                  data-testid="action-mark-coi"
                  onSelect={async () => {
                    const r = await fetch(`/api/cois/${contact.id}/mark`, { method: "POST", credentials: "include" });
                    if (!r.ok) { toast.error("Failed to mark as COI"); return; }
                    queryClient.invalidateQueries({ queryKey: ["cois"] });
                    queryClient.invalidateQueries({ queryKey: ["coi-firm"] });
                    toast.success(`${contact.name} is now a COI`);
                    setLocation(`/coi/${contact.id}`);
                  }}
                >
                  <Handshake className="w-4 h-4 mr-2" />
                  Mark as COI
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid="action-add-deal"
                disabled={!contact.companyId}
                onSelect={(e) => {
                  if (!contact.companyId) return;
                  e.preventDefault();
                  setAddToDealOpen(true);
                }}
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Add to deal
                {!contact.companyId && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    (link a company first)
                  </span>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="action-export"
                onSelect={(e) => {
                  e.preventDefault();
                  handleExport();
                }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Export as vCard
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                data-testid="action-delete"
                onSelect={(e) => {
                  // onSelect fires on both click and keyboard activation.
                  // Prevent the default close-then-close race so the dialog
                  // opens cleanly after the dropdown dismisses.
                  e.preventDefault();
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete contact
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </ActionBar>
      }
    />
  );

  const work = (
    <>
      <BriefPanel
        summary={summary.data}
        loading={summary.isLoading}
        error={summary.isError}
        onRetry={() => summary.refetch()}
        openLabel={`Open with ${firstName(contact.name)}`}
        target={{ contactId: contact.id, companyId: contact.companyId ?? undefined }}
        nextStepOpen={nextStepOpen}
        onNextStepOpenChange={setNextStepOpen}
      />
      {summary.data?.processes && <ProcessesPanel rows={summary.data.processes} title="In processes" />}
      <RecordPanel id="record-activity">
        <ActivityTimeline
          scope={{ kind: "contact", contactId: contact.id, contactName: contact.name }}
          compose={compose}
          onComposeChange={setCompose}
        />
      </RecordPanel>
    </>
  );

  const rail = (
    <>
      {company && <RailPeople title={`At ${company.name}`} people={colleagues} />}
      <RailFtcContact rows={summary.data?.ftcContact} loading={summary.isLoading} error={summary.isError} />
      <RailDetails rows={[
        ["Added", new Date(contact.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })],
        ["Source", sourceLabel(contact.creationSource)],
        ["Created by", contact.creator?.name || "Unknown"],
      ]} />
      <RailNotes value={contact.notes} onSave={(v) => saveContactFieldAsync("notes", v.trim() || null)} />
      <RailDocuments contactId={contact.id} />
    </>
  );

  return (
    <AppLayout title={contact.name}>
      <RecordFrame
        header={header}
        facts={summary.data
          ? <FactsStrip facts={contactFacts(summary.data, today, company ? { name: company.name, category: company.category } : null)} />
          : summary.isLoading ? <div className="h-[58px] animate-pulse rounded-lg border bg-card" /> : null}
        work={work}
        rail={rail}
      />

      <EditContactDrawer
        allowAdvocate={category === "Client"}
        open={editOpen}
        onOpenChange={setEditOpen}
        contact={contact}
      />

      <ChangeCompanyDialog
        open={changeCompanyOpen}
        onOpenChange={setChangeCompanyOpen}
        currentCompanyId={contact.companyId}
        title={contact.companyId ? "Move contact to a different company" : "Link contact to a company"}
        description={`Search by name. ${contact.name} will be reassigned to the company you pick.`}
        onSelect={handleCompanyPick}
      />

      {contact.companyId && (
        <AddToDealDialog
          open={addToDealOpen}
          onOpenChange={setAddToDealOpen}
          companyId={contact.companyId}
          contactName={contact.name}
          companyCategory={company?.category}
          onLinked={() => {
            // Refresh any views that might care: the contact's company
            // detail page (shows dealLinks), the deal list itself, and
            // this contact (in case we ever surface deal context here).
            queryClient.invalidateQueries({ queryKey: ["company", contact.companyId] });
            queryClient.invalidateQueries({ queryKey: ["company-deals", contact.companyId] });
            queryClient.invalidateQueries({ queryKey: ["deals-picker"] });
          }}
        />
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={(open) => !isDeleting && setDeleteDialogOpen(open)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete {contact.name}?</DialogTitle>
            <DialogDescription>
              This permanently removes the contact from the CRM. Activity and
              email history that references them will keep their records but
              the contact row itself cannot be recovered.
              {company?.name ? ` The company (${company.name}) is unaffected.` : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              data-testid="confirm-delete-contact"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete contact
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
